// El catálogo del portal completo para la lista POS de «Teléfono». Pinta lo que ya llegó mientras
// el client encadena las páginas que faltan.
import { useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { wizardApi } from '@/api/services/retail-wizard';
import type { PortalCatalogAll } from '@/api/services/retail-wizard/contract';

export const PORTAL_CATALOG_ALL_KEY = ['sales-portal', 'catalog', 'all'] as const;

const STALE_TIME_MS = 60_000;

/** Deja en caché lo acumulado; al refrescar no pisa una lista completa con una parcial. */
function fetchPortalCatalogAll({
    client,
    queryKey,
}: QueryFunctionContext<typeof PORTAL_CATALOG_ALL_KEY>): Promise<PortalCatalogAll> {
    return wizardApi.listPortalCatalogAll((partial) => {
        if (client.getQueryData<PortalCatalogAll>(queryKey)?.complete === true) return;
        client.setQueryData(queryKey, partial);
    });
}

export interface PortalCatalogAllResult {
    /** Lo completo, o lo acumulado mientras faltan páginas; `undefined` antes de la primera. */
    data: PortalCatalogAll | undefined;
    /** Todavía no llega ni la primera página. */
    isLoading: boolean;
    isError: boolean;
    /** Una página siguiente falló: lo que hay es lo que alcanzó a llegar. */
    failedMidway: boolean;
    refetch: () => void;
}

export function usePortalCatalogAll(): PortalCatalogAllResult {
    const query = useQuery({
        queryKey: PORTAL_CATALOG_ALL_KEY,
        queryFn: fetchPortalCatalogAll,
        staleTime: STALE_TIME_MS,
    });
    const data = query.data;
    return {
        data,
        isLoading: query.isPending,
        isError: query.isError && data === undefined,
        failedMidway: query.isError && data !== undefined && !data.complete,
        refetch: () => {
            void query.refetch();
        },
    };
}
